import React from "react";
import { observer } from "mobx-react";
import { computed } from "mobx";
import { generatePath, withRouter, RouteComponentProps } from "react-router";
import InfiniteScroll from "react-infinite-scroll-component";

import AddIcon from "@material-ui/icons/Add";
import { Button } from "@material-ui/core";

import Page from "components/Layout/Page";
import Placeholder from "components/Placeholder";
import FilteringControl from "components/FilteringControl";
import SortingControl from "components/SortingControl";
import withGlobalPopup, {
  GlobalPopupProps,
} from "components/Popup/withGlobalPopup";
import {
  withGlobalSnackbar,
  SnackbarContextProps,
} from "services/snackbar/snackbarContext";

import NavBar from "../NavBar";
import CoachCard from "./CoachCard";

import { SNACKBAR_TYPES } from "components/Snackbar";

import ROUTES from "navigation/routes";

import { TableSortType } from "stores/templates/TableStore";

import {
  PageControl,
  SearchContainer,
  SearchControls,
  SearchControlWrapper,
  Search,
  CoachCardWrapper,
  OverflowLoader,
  ScrollLoader,
} from "./styles";
import CoachStore from "stores/CoachStore";

type Props = RouteComponentProps & GlobalPopupProps & SnackbarContextProps & {};

const HEADER_WITH_TITLE_HEIGHT = "338px";
const MINIMUM_SEARCH_VALUE_LENGTH = 3;

@observer
class Coach extends React.Component<Props> {
  @computed get coaches() {
    return CoachStore.items;
  }

  @computed get coachListIsEmpty() {
    return this.coaches.length === 0;
  }

  componentDidMount() {
    CoachStore.fetch();
  }

  componentWillUnmount() {
    CoachStore.resetPage();
  }

  fetchMoreCoaches = async () => {
    const { setMessage } = this.props;

    try {
      await CoachStore.fetchMoreCoaches();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onSearchApply = (value: string) => {
    if (value.length === 0) {
      this.onSearchClear();
      return;
    }

    CoachStore.applySearch(value);
    value.length >= MINIMUM_SEARCH_VALUE_LENGTH && CoachStore.fetch();
  };

  onSearchClear = () => {
    CoachStore.clearSearch();
    CoachStore.fetch();
  };

  onSortApply = (value: TableSortType) => {
    CoachStore.applyOrder(value);
    CoachStore.resetPage();
    CoachStore.fetch();
  };

  onFilterApply = (
    filterValues: { column: string; value: (string | number)[] }[],
  ) => {
    filterValues.map(filterValue =>
      CoachStore.applyFilter({
        column: filterValue.column,
        value: filterValue.value,
      }),
    );
    CoachStore.resetPage();
    CoachStore.fetch();
  };

  onFilterClear = () => {
    CoachStore.clearFilters();
    CoachStore.fetch();
  };

  onAddCoach = () => {
    this.props.history.push({
      pathname: generatePath(ROUTES.CONTENT_PERSONAL_COACH_DETAIL),
      state: { editMode: true },
    });
  };

  onDetailsClick = (coach: any) => () => {
    const path = generatePath(ROUTES.CONTENT_PERSONAL_COACH_DETAIL, {
      coachId: coach.id,
    });
    this.props.history.push({ pathname: path, state: { editMode: false } });
  };

  onEdit = (coach: any) => () => {
    const path = generatePath(ROUTES.CONTENT_PERSONAL_COACH_DETAIL, {
      coachId: coach.id,
    });
    this.props.history.push({ pathname: path, state: { editMode: true } });
  };

  onPublish = (coach: any) => async () => {
    const { setMessage } = this.props;
    const { id, publishStatus } = coach;

    try {
      await CoachStore.changeCoachStatus(id, publishStatus);
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  render() {
    return (
      <Page
        title="Coaches"
        topControls={<NavBar />}
        controls={
          <>
            <PageControl>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={this.onAddCoach}
              >
                Add Coach
              </Button>
            </PageControl>
          </>
        }
        isLoading={CoachStore.isLoading}
      >
        <SearchContainer>
          <Search
            initValue={CoachStore.search}
            onApply={this.onSearchApply}
            onClear={this.onSearchClear}
          />
          <SearchControls>
            <SearchControlWrapper>
              <SortingControl
                initValue={CoachStore.orderContext.appliedOrderInfo}
                onApply={this.onSortApply}
              />
            </SearchControlWrapper>
            <SearchControlWrapper>
              <FilteringControl
                initValue={CoachStore.filters}
                onApply={this.onFilterApply}
                onClear={this.onFilterClear}
                statusFiltering
              />
            </SearchControlWrapper>
          </SearchControls>
        </SearchContainer>

        {(CoachStore.isCoachPublishing || CoachStore.isCoachCopying) && (
          <OverflowLoader />
        )}
        {!this.coachListIsEmpty ? (
          <InfiniteScroll
            dataLength={this.coaches.length}
            hasMore={CoachStore.hasMoreInfo}
            next={this.fetchMoreCoaches}
            height={`calc(100vh - ${HEADER_WITH_TITLE_HEIGHT})`}
            loader={<ScrollLoader />}
          >
            {this.coaches.map(coach => (
              <CoachCardWrapper key={coach.id}>
                <CoachCard
                  title={coach.title}
                  image={coach.photo || ""}
                  publishStatus={coach.publishStatus}
                  onDetailsClick={this.onDetailsClick(coach)}
                  onEdit={this.onEdit(coach)}
                  onPublish={this.onPublish(coach)}
                />
              </CoachCardWrapper>
            ))}
          </InfiniteScroll>
        ) : (
          <Placeholder
            height={280}
            width={280}
            text={
              CoachStore.search
                ? "No matches found. Please try another search."
                : "No coaches added yet."
            }
          />
        )}
      </Page>
    );
  }
}

export default withGlobalPopup(withGlobalSnackbar(withRouter(Coach)));
